import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

const AuroraAmber = ({ 
  intensity = 0.2, 
  className = "" 
}) => {
  const canvasRef = useRef(null);
  const animationRef = useRef();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Create animated orbs
    const orbs = Array.from({ length: 8 }, (_, i) => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.5,
      vy: (Math.random() - 0.5) * 0.3,
      radius: Math.random() * 100 + 50,
      hue: Math.random() * 40 + 40, // Amber to yellow range
      saturation: 60 + Math.random() * 40,
      lightness: 70 + Math.random() * 20,
      alpha: intensity * (0.3 + Math.random() * 0.4)
    }));

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      orbs.forEach(orb => {
        // Update position
        orb.x += orb.vx;
        orb.y += orb.vy;

        // Bounce off edges
        if (orb.x < 0 || orb.x > canvas.width) orb.vx *= -1;
        if (orb.y < 0 || orb.y > canvas.height) orb.vy *= -1;

        // Create gradient
        const gradient = ctx.createRadialGradient(
          orb.x, orb.y, 0,
          orb.x, orb.y, orb.radius
        );

        gradient.addColorStop(0, `hsla(${orb.hue}, ${orb.saturation}%, ${orb.lightness}%, ${orb.alpha})`);
        gradient.addColorStop(0.5, `hsla(${orb.hue + 10}, ${orb.saturation}%, ${orb.lightness + 10}%, ${orb.alpha * 0.6})`);
        gradient.addColorStop(1, `hsla(${orb.hue + 20}, ${orb.saturation}%, ${orb.lightness + 20}%, 0)`);

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(orb.x, orb.y, orb.radius, 0, Math.PI * 2);
        ctx.fill();
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [intensity]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1, ease: "easeOut" }}
      className={`fixed inset-0 pointer-events-none ${className}`}
      style={{ zIndex: -1 }}
    >
      <canvas
        ref={canvasRef}
        className="w-full h-full"
        style={{ opacity: intensity }}
      />
      
      {/* Subtle overlay for extra warmth */}
      <div 
        className="absolute inset-0"
        style={{
          background: `radial-gradient(circle at 30% 20%, rgba(255, 213, 79, ${intensity * 0.3}) 0%, transparent 50%), 
                      radial-gradient(circle at 70% 80%, rgba(255, 193, 7, ${intensity * 0.2}) 0%, transparent 50%)`
        }}
      />
    </motion.div>
  );
};

export default AuroraAmber;



